// Floating alignment panel (F-03.4–F-03.9): the version to align, auto-align, three presets, manual fine-tuning.

import type { App } from '../app/app';
import { coverageInfo, transformsEqual } from '../core/alignment';
import type { AlignMode, SourceId } from '../core/types';
import { B_IDS, slotLabel } from '../core/types';
import { t } from '../i18n';
import { $, esc, segmented, setText } from './dom';
import { edgesText } from './badges';

/** Presets offered in the panel; "top-left" is rare and done with manual fine-tuning (F-03.9). */
const PANEL_PRESETS = ['center', 'stretch', 'fit'] as const;
type PanelPreset = (typeof PANEL_PRESETS)[number];

let updSeg: ReturnType<typeof segmented<PanelPreset>>;
let updTarget: ReturnType<typeof segmented<SourceId>>;
const r1 = (v: number) => Math.round(v * 10) / 10;
const pct = (v: number) => (Math.round(v * 10000) / 100).toFixed(2);

export function initAlignPanel(app: App): void {
  const sel = () => app.project.layout.selectedB;
  // The version to align: works in every layout, also where the toolbar's B / C / D buttons are hidden.
  updTarget = segmented($('#segAlignTarget'), B_IDS, (b) => slotLabel(b), (b) => app.selectB(b));
  updSeg = segmented($('#segAlign'), PANEL_PRESETS, (m) => t('align.mode.' + m), (m) => app.applyAlignPreset(sel(), m), (m) => t('align.mode.' + m + '.tip'));
  $('#alignClose').addEventListener('click', () =>
    app.store.ui((u) => {
      u.alignOpen = false;
      u.alignPick = null;
    }),
  );
  // Two-point alignment (F-03.11).
  $('#alignPoints').addEventListener('click', () => (app.state.ui.alignPick ? app.cancelPointAlign() : app.startPointAlign(sel())));
  $('#alignPickReset').addEventListener('click', () => app.store.ui((u) => u.alignPick && (u.alignPick = { ...u.alignPick, a: [], b: [] })));
  $('#alignPickCancel').addEventListener('click', () => app.cancelPointAlign());
  const num = (id: string, key: 'dx' | 'dy' | 'sx' | 'sy', isPct: boolean) => {
    const el = $<HTMLInputElement>(id);
    el.addEventListener('input', () => {
      const v = Number(el.value);
      if (!Number.isFinite(v)) return;
      if (isPct && v <= 0) return;
      app.setTransform(sel(), { [key]: isPct ? v / 100 : v });
    });
  };
  num('#alDx', 'dx', false);
  num('#alDy', 'dy', false);
  num('#alSx', 'sx', true);
  num('#alSy', 'sy', true);
  $('#alignAuto').addEventListener('click', () => void app.autoAlign(sel()));
  $('#alignReset').addEventListener('click', () => app.applyAlignPreset(sel(), 'center'));
  const diff = $<HTMLInputElement>('#alignShowDiff');
  diff.addEventListener('change', () => app.store.settings((s) => (s.alignAutoDifference = diff.checked)));
  $('#alignSuggest').addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('#alignApply')) app.applySuggestion(sel());
  });
}

/** "Stretch to A + shift (3, −1)" / "X 77.51 % Y 77.49 % · shift 3, −1": what a transform does, in one line. */
function describe(tr: { mode: AlignMode; dx: number; dy: number; sx: number; sy: number }, free: boolean): string {
  const shift = Math.abs(tr.dx) >= 0.05 || Math.abs(tr.dy) >= 0.05 ? t('align.shift', { dx: r1(tr.dx), dy: r1(tr.dy) }) : '';
  if (free || tr.mode === 'manual') return t('align.freeResult', { sx: pct(tr.sx), sy: pct(tr.sy), dx: r1(tr.dx), dy: r1(tr.dy) });
  return [t('align.mode.' + tr.mode), shift].filter(Boolean).join(' + ');
}

export function updateAlignPanel(app: App): void {
  const panel = $('#alignPanel');
  const open = app.state.ui.alignOpen;
  panel.hidden = !open;
  if (!open) return;
  const id = app.project.layout.selectedB;
  const a = app.sources.source('A');
  const b = app.sources.source(id);
  setText($('#alignTitle'), t('align.title'));
  updTarget(app.sources.has(id) ? id : null, (x) => !app.sources.has(x));
  const ready = !!a && !!b && app.sources.has(id);
  $<HTMLButtonElement>('#alignAuto').disabled = !ready || app.autoAligning.has(id);
  const pick = app.state.ui.alignPick;
  const pb = $<HTMLButtonElement>('#alignPoints');
  pb.disabled = !ready;
  pb.classList.toggle('on', !!pick);
  $('#alignPick').hidden = !pick;
  if (pick) setText($('#alignPickText'), t('align.points.guide', { b: slotLabel(pick.id), na: pick.a.length, nb: pick.b.length }));
  if (!ready) {
    setText($('#alignInfo'), t('align.needB'));
    $('#alignSuggest').innerHTML = '';
    return;
  }
  const same = a.spec.width === b.spec.width && a.spec.height === b.spec.height;
  const scale = Math.round((a.spec.width / b.spec.width) * 1000) / 10;
  setText($('#alignInfo'), same ? t('align.infoSame', { id, w: b.spec.width, h: b.spec.height }) : t('align.info', { id, bw: b.spec.width, bh: b.spec.height, aw: a.spec.width, ah: a.spec.height, p: scale }));
  const tr = b.spatialTransform;
  updSeg(PANEL_PRESETS.includes(tr.mode as PanelPreset) ? (tr.mode as PanelPreset) : null);

  // Result line: running, the applied / suggested alignment with its difference, or not aligned yet.
  const sg = app.sources.suggestions.get(id);
  const box = $('#alignSuggest');
  let html: string;
  if (app.autoAligning.has(id) || app.sources.estimating.has(id) || app.freeEstimating.has(id)) html = `<div class="muted">${esc(t('align.autoRunning'))}</div>`;
  else if (sg) {
    const applied = transformsEqual(sg.transform, tr, 0.05);
    html =
      `<div><b>${esc(applied ? t('align.applied') : t('align.suggestion'))}</b> ${esc(describe(sg.transform, !!sg.free))}</div>` +
      `<div class="muted small">${esc(t('align.errorValue', { e: sg.error.toFixed(2) }))}</div>` +
      (applied ? '' : `<div class="row2"><button id="alignApply">${esc(t('align.apply'))}</button></div>`);
    box.className = 'suggest' + (applied ? '' : ' hot');
  } else {
    html = `<div class="muted small">${esc(t(same ? 'align.sameSize' : 'align.notAligned'))}</div>`;
    box.className = 'suggest';
  }
  if (box.innerHTML !== html) box.innerHTML = html;

  const setNum = (sel: string, v: string) => {
    const el = $<HTMLInputElement>(sel);
    if (document.activeElement !== el && el.value !== v) el.value = v;
  };
  setNum('#alDx', String(r1(tr.dx)));
  setNum('#alDy', String(r1(tr.dy)));
  setNum('#alSx', pct(tr.sx));
  setNum('#alSy', pct(tr.sy));
  // Crop / black edges only when there are any.
  const c = coverageInfo(tr, { w: a.spec.width, h: a.spec.height }, { w: b.spec.width, h: b.spec.height });
  const crop = edgesText({ l: c.cropLeft, r: c.cropRight, t: c.cropTop, b: c.cropBottom });
  const gap = edgesText({ l: c.blackLeft, r: c.blackRight, t: c.blackTop, b: c.blackBottom });
  setText($('#alignEdges'), [crop ? t('align.crop', { v: crop }) : '', gap ? t('align.gap', { v: gap }) : ''].filter(Boolean).join(' · '));
  $<HTMLInputElement>('#alignShowDiff').checked = app.state.settings.alignAutoDifference;
}
